import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageHeader from './components/PageHeader';
import { apiFetch, apiUrl } from './utils/api';

const API_BASE = apiUrl('/api/marketing/images');

const CATEGORIES = [
  { value: 'all', label: 'All Images' },
  { value: 'logo', label: 'Logo / Brand' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'domestic', label: 'Residential / Domestic' },
  { value: 'doors', label: 'Automatic Doors' },
  { value: 'gates', label: 'Electric Gates' },
  { value: 'barriers', label: 'Barriers' },
  { value: 'access-control', label: 'Access Control' },
  { value: 'newsletter', label: 'Newsletter / Blog' },
  { value: 'general', label: 'General' },
];

const editableCategories = CATEGORIES.filter(category => category.value !== 'all');

function formatFileSize(size = 0) {
  if (size >= 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(size / 1024))} KB`;
}

function imageCategoryLabel(value) {
  return CATEGORIES.find(category => category.value === value)?.label || 'General';
}

export default function MarketingImageLibrary() {
  const fileInputRef = useRef(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [uploadCategory, setUploadCategory] = useState('logo');
  const [uploadAsPrimaryLogo, setUploadAsPrimaryLogo] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ label: '', category: 'general' });
  const [copiedUrl, setCopiedUrl] = useState('');
  const [generatePrompt, setGeneratePrompt] = useState('');
  const [generateLabel, setGenerateLabel] = useState('');
  const [generateCategory, setGenerateCategory] = useState('newsletter');
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState('');

  const fetchImages = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(API_BASE);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unable to load image library');
      setImages(data.images || []);
    } catch (err) {
      setError(err.message || 'Unable to load image library');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const filteredImages = useMemo(() => {
    const term = query.trim().toLowerCase();
    return images.filter(image => {
      if (filter !== 'all' && (image.category || 'general') !== filter) return false;
      if (!term) return true;
      return [image.label, image.filename, image.originalName, image.category]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(term));
    });
  }, [filter, images, query]);

  const primaryLogo = images.find(image => image.isPrimaryLogo);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('label', file.name.replace(/\.[^.]+$/, ''));
      formData.append('category', uploadCategory);
      formData.append('isPrimaryLogo', uploadAsPrimaryLogo && uploadCategory === 'logo' ? 'true' : 'false');

      const res = await apiFetch(API_BASE, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      await fetchImages();
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const startEdit = (image) => {
    setEditing(image.filename);
    setEditForm({
      label: image.label || image.filename,
      category: image.category || 'general',
    });
  };

  const saveMetadata = async (image, overrides = null) => {
    setError('');
    const payload = overrides || {
      label: editForm.label || image.label || image.filename,
      category: editForm.category || image.category || 'general',
    };

    try {
      const res = await apiFetch(`${API_BASE}/${encodeURIComponent(image.filename)}/rename`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const updated = await res.json();
      if (!res.ok) throw new Error(updated.error || 'Update failed');
      setImages(prev => prev.map(item => item.filename === image.filename ? updated : {
        ...item,
        isPrimaryLogo: payload.isPrimaryLogo ? false : item.isPrimaryLogo,
      }));
      setEditing(null);
    } catch (err) {
      setError(err.message || 'Update failed');
    }
  };

  const handleDelete = async (image) => {
    if (!window.confirm(`Delete "${image.label || image.filename}" from the Doorbase image library?`)) return;

    setError('');
    try {
      const res = await apiFetch(`${API_BASE}/${encodeURIComponent(image.filename)}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      setImages(prev => prev.filter(item => item.filename !== image.filename));
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  };

  const copyUrl = async (image) => {
    try {
      await navigator.clipboard.writeText(image.url);
      setCopiedUrl(image.url);
      window.setTimeout(() => setCopiedUrl(''), 1800);
    } catch {
      setError('Could not copy URL to clipboard');
    }
  };

  const setPrimaryLogo = (image) => {
    saveMetadata(image, {
      label: image.label || image.filename,
      category: 'logo',
      isPrimaryLogo: true,
    });
  };

  const generateImage = async () => {
    const prompt = generatePrompt.trim();
    if (!prompt) {
      setError('Enter an image prompt before generating.');
      return;
    }

    setGenerating(true);
    setError('');
    setMessage('');
    try {
      const res = await apiFetch(`${API_BASE}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt,
          topic: generateLabel.trim() || prompt,
          label: generateLabel.trim() || 'Generated marketing image',
          category: generateCategory,
          audience: generateCategory,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.details || data.error || 'Unable to generate image');
      setMessage(`Generated "${data.label || 'image'}" and added it to the library.`);
      setGeneratePrompt('');
      setGenerateLabel('');
      setFilter(generateCategory);
      await fetchImages();
    } catch (err) {
      setError(err.message || 'Unable to generate image');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Image Library"
        subtitle="Manage Doorbase-hosted marketing images on Render persistent disk"
        backTo="/marketing-hub"
        backLabel="Marketing Hub"
        sticky
      />

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <section className="bg-white border border-gray-200 rounded-lg shadow-sm p-5">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">Doorbase Marketing Assets</h2>
              <p className="text-sm text-gray-500 mt-1">
                Images uploaded here are served by Doorbase and can be used in Master Templates, newsletters, campaigns and blog content.
              </p>
              {primaryLogo && (
                <p className="text-sm text-[#172378] mt-2">
                  Primary logo: <span className="font-semibold">{primaryLogo.label}</span>
                </p>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <select
                  value={uploadCategory}
                  onChange={event => {
                    setUploadCategory(event.target.value);
                    if (event.target.value !== 'logo') setUploadAsPrimaryLogo(false);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                >
                  {editableCategories.map(category => (
                    <option key={category.value} value={category.value}>{category.label}</option>
                  ))}
                </select>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={uploadAsPrimaryLogo}
                    onChange={event => setUploadAsPrimaryLogo(event.target.checked)}
                    disabled={uploadCategory !== 'logo'}
                    className="rounded border-gray-300 text-[#172378]"
                  />
                  Primary logo
                </label>
              </div>
              <label className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-[#172378] text-white text-sm font-semibold hover:bg-[#0f185b] cursor-pointer">
                {uploading ? 'Uploading...' : 'Upload Image'}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                  onChange={handleUpload}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            </div>
          </div>
        </section>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
            {error}
          </div>
        )}
        {message && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
            {message}
          </div>
        )}

        <section className="bg-white border border-gray-200 rounded-lg shadow-sm p-5">
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Generate Image</h2>
              <p className="text-sm text-gray-500 mt-1">
                Create a new marketing image using the provider configured in Operations Setup.
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
              <label className="lg:col-span-2 space-y-1">
                <span className="text-xs font-semibold text-gray-600 uppercase">Prompt</span>
                <textarea
                  value={generatePrompt}
                  onChange={event => setGeneratePrompt(event.target.value)}
                  rows={3}
                  placeholder="Residential driveway gate at a modern UK home, realistic daylight photography"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </label>
              <div className="space-y-3">
                <label className="block space-y-1">
                  <span className="text-xs font-semibold text-gray-600 uppercase">Label</span>
                  <input
                    value={generateLabel}
                    onChange={event => setGenerateLabel(event.target.value)}
                    placeholder="Optional"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </label>
                <label className="block space-y-1">
                  <span className="text-xs font-semibold text-gray-600 uppercase">Category</span>
                  <select
                    value={generateCategory}
                    onChange={event => setGenerateCategory(event.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                  >
                    {editableCategories.map(category => (
                      <option key={category.value} value={category.value}>{category.label}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="flex lg:items-end">
                <button
                  type="button"
                  onClick={generateImage}
                  disabled={generating || !generatePrompt.trim()}
                  className="w-full px-4 py-2 rounded-md bg-[#172378] text-white text-sm font-semibold hover:bg-[#0f185b] disabled:opacity-60"
                >
                  {generating ? 'Generating...' : 'Generate Image'}
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <input
              type="search"
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Search images by label, filename or category..."
              className="w-full md:max-w-md px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            <select
              value={filter}
              onChange={event => setFilter(event.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
            >
              {CATEGORIES.map(category => (
                <option key={category.value} value={category.value}>{category.label}</option>
              ))}
            </select>
          </div>
        </section>

        {loading ? (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12 text-center text-gray-500">
            Loading image library...
          </div>
        ) : filteredImages.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12 text-center">
            <h3 className="text-lg font-semibold text-gray-800">No images found</h3>
            <p className="text-sm text-gray-500 mt-2">
              Upload the ADG logo and marketing imagery to start building the managed asset library.
            </p>
          </div>
        ) : (
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filteredImages.map(image => {
              const isEditing = editing === image.filename;
              return (
                <article key={image.filename} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                  <div className="aspect-square bg-gray-100 flex items-center justify-center p-4">
                    <img src={image.url} alt={image.label} className="max-w-full max-h-full object-contain" />
                  </div>
                  <div className="p-4 space-y-3">
                    {isEditing ? (
                      <div className="space-y-2">
                        <input
                          value={editForm.label}
                          onChange={event => setEditForm(prev => ({ ...prev, label: event.target.value }))}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                          autoFocus
                        />
                        <select
                          value={editForm.category}
                          onChange={event => setEditForm(prev => ({ ...prev, category: event.target.value }))}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm bg-white"
                        >
                          {editableCategories.map(category => (
                            <option key={category.value} value={category.value}>{category.label}</option>
                          ))}
                        </select>
                        <div className="flex gap-2">
                          <button onClick={() => saveMetadata(image)} className="flex-1 px-3 py-1.5 bg-[#172378] text-white rounded text-sm font-semibold">Save</button>
                          <button onClick={() => setEditing(null)} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-sm">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div>
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-semibold text-gray-900 text-sm leading-tight">{image.label}</h3>
                            {image.isPrimaryLogo && (
                              <span className="shrink-0 text-[11px] px-2 py-0.5 rounded-full bg-[#fc8f00]/15 text-[#a45b00] font-semibold">
                                Primary logo
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{imageCategoryLabel(image.category)} · {formatFileSize(image.size)}</p>
                          <p className="text-[11px] text-gray-400 mt-1 truncate" title={image.filename}>{image.filename}</p>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <button onClick={() => startEdit(image)} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200">Edit</button>
                          <button onClick={() => copyUrl(image)} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200">
                            {copiedUrl === image.url ? 'Copied' : 'Copy URL'}
                          </button>
                          <button onClick={() => setPrimaryLogo(image)} className="px-3 py-1.5 bg-blue-50 text-[#172378] rounded text-sm hover:bg-blue-100">Set Logo</button>
                          <button onClick={() => handleDelete(image)} className="px-3 py-1.5 bg-red-50 text-red-700 rounded text-sm hover:bg-red-100">Delete</button>
                        </div>
                      </>
                    )}
                  </div>
                </article>
              );
            })}
          </section>
        )}
      </main>
    </div>
  );
}
